import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FOLLOWUPS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DigestSettings from "@/components/DigestSettings";
import {
  BellRing,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Mail,
  MessageCircle,
  Linkedin,
  Trash2,
} from "lucide-react";

const SHARE_TOKENS_KEY = "imali:share-tokens";
const loadShareTokens = () => {
  try {
    return JSON.parse(localStorage.getItem(SHARE_TOKENS_KEY) || "{}");
  } catch {
    return {};
  }
};

const CHANNEL_ICON = {
  email: Mail,
  whatsapp: MessageCircle,
  linkedin: Linkedin,
  copy: null,
};

const MS_PER_MIN = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MIN;
const MS_PER_DAY = 24 * MS_PER_HOUR;

function relativeDue(remindAtIso) {
  const target = new Date(remindAtIso).getTime();
  const now = Date.now();
  const diffMs = target - now;
  const abs = Math.abs(diffMs);
  let human;
  if (abs > MS_PER_DAY) human = `${Math.round(abs / MS_PER_DAY)}d`;
  else if (abs > MS_PER_HOUR) human = `${Math.round(abs / MS_PER_HOUR)}h`;
  else human = `${Math.max(1, Math.round(abs / MS_PER_MIN))}m`;
  return diffMs < 0 ? `Overdue by ${human}` : `Due in ${human}`;
}

export default function FollowUps() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  const tokens = useMemo(() => loadShareTokens(), []);
  const shareIds = useMemo(() => Object.keys(tokens), [tokens]);

  const load = useCallback(async () => {
    if (shareIds.length === 0) {
      setReminders([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const results = await Promise.allSettled(
      shareIds.map((id) =>
        api.get(`/reminders?share_id=${id}`, {
          headers: { "X-Owner-Token": tokens[id] },
        })
      )
    );
    const all = [];
    for (const r of results) {
      if (r.status === "fulfilled") {
        for (const rem of r.value.data) {
          all.push({ ...rem, owner_token: tokens[rem.share_id] });
        }
      }
    }
    all.sort((a, b) => new Date(a.remind_at) - new Date(b.remind_at));
    setReminders(all);
    setLoading(false);
  }, [shareIds, tokens]);

  useEffect(() => {
    load();
  }, [load]);

  const markDone = async (rem) => {
    try {
      await api.patch(`/reminders/${rem.id}/done`, null, {
        headers: { "X-Owner-Token": rem.owner_token },
      });
      setReminders((prev) => prev.filter((r) => r.id !== rem.id));
      window.dispatchEvent(new CustomEvent("imali:reminders-changed"));
      toast.success("Marked done");
    } catch {
      toast.error("Unable to mark done");
    }
  };
  const del = async (rem) => {
    if (!window.confirm("Delete this reminder?")) return;
    try {
      await api.delete(`/reminders/${rem.id}`, {
        headers: { "X-Owner-Token": rem.owner_token },
      });
      setReminders((prev) => prev.filter((r) => r.id !== rem.id));
      window.dispatchEvent(new CustomEvent("imali:reminders-changed"));
      toast.success("Reminder deleted");
    } catch {
      toast.error("Unable to delete");
    }
  };

  const overdue = reminders.filter((r) => new Date(r.remind_at) < new Date());
  const later = reminders.filter((r) => new Date(r.remind_at) >= new Date());

  return (
    <div
      data-testid={FOLLOWUPS.page}
      className="max-w-4xl mx-auto px-6 lg:px-10 py-14"
    >
      <div className="max-w-2xl">
        <p className="text-xs tracking-[0.28em] uppercase text-secondary font-semibold mb-4">
          Follow-ups
        </p>
        <h1 className="font-display text-4xl lg:text-6xl leading-none tracking-tighter font-light">
          Your <span className="text-primary">lead queue</span>.
        </h1>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
          Every borrower you snoozed from a share analytics timeline lands
          here. Reach out when the time is right — Imali will keep track of
          who&apos;s still waiting.
        </p>
      </div>

      {loading ? (
        <div className="mt-14 flex items-center gap-3 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading reminders…
        </div>
      ) : reminders.length === 0 ? (
        <>
          <div className="mt-10">
            <DigestSettings />
          </div>
          <div
            data-testid={FOLLOWUPS.empty}
            className="mt-10 rounded-3xl border border-dashed border-border p-12 text-center bg-card"
          >
            <BellRing
              aria-hidden="true"
              className="w-10 h-10 text-primary mx-auto"
            />
            <h2 className="mt-4 font-display text-2xl tracking-tight">
              No follow-ups yet
            </h2>
            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
              When you share a comparison and someone opens it, you can snooze a
              follow-up from the analytics recent-hits list.
            </p>
            <Link to="/compare">
              <Button className="mt-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                Draft a comparison
              </Button>
            </Link>
          </div>
        </>
      ) : (
        <div className="mt-12 space-y-10">
          <DigestSettings />
          {overdue.length > 0 && (
            <Group title="Overdue" tone="destructive" reminders={overdue} markDone={markDone} del={del} />
          )}
          <Group
            title={overdue.length ? "Upcoming" : "All reminders"}
            reminders={later}
            markDone={markDone}
            del={del}
          />
        </div>
      )}
    </div>
  );
}

function Group({ title, tone, reminders, markDone, del }) {
  if (reminders.length === 0) return null;
  return (
    <section>
      <div className="flex items-baseline gap-3 border-b border-border pb-2">
        <h2
          className={`font-display text-2xl tracking-tight ${
            tone === "destructive" ? "text-destructive" : ""
          }`}
        >
          {title}
        </h2>
        <span className="font-mono text-xs text-muted-foreground">
          {reminders.length}
        </span>
      </div>
      <ul className="mt-5 space-y-3">
        {reminders.map((rem) => (
          <ReminderRow key={rem.id} rem={rem} markDone={markDone} del={del} />
        ))}
      </ul>
    </section>
  );
}

function ReminderRow({ rem, markDone, del }) {
  const ChannelIcon = CHANNEL_ICON[rem.channel] || Mail;
  const overdue = new Date(rem.remind_at) < new Date();
  return (
    <li
      data-testid={FOLLOWUPS.row(rem.id)}
      className={`rounded-2xl border p-5 bg-card ${
        overdue ? "border-destructive/40" : "border-border"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-lg tracking-tight">
              {rem.business_name}
            </h3>
            <Badge variant="outline" className="text-[10px]">
              {rem.industry_name}
            </Badge>
            {rem.target_ref_host && (
              <Badge variant="outline" className="text-[10px] font-mono">
                {rem.target_ref_host}
              </Badge>
            )}
          </div>
          {rem.note && (
            <p className="mt-2 text-sm text-muted-foreground line-clamp-2 whitespace-pre-line">
              {rem.note}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span
              className={`font-mono ${
                overdue ? "text-destructive" : "text-primary"
              }`}
            >
              {relativeDue(rem.remind_at)}
            </span>
            <span className="text-muted-foreground font-mono">
              {new Date(rem.remind_at).toLocaleString()}
            </span>
            {rem.channel && (
              <span className="text-muted-foreground inline-flex items-center gap-1">
                <ChannelIcon aria-hidden="true" className="w-3 h-3" />
                {rem.channel}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link to={`/c/${rem.share_id}`}>
            <Button
              data-testid={FOLLOWUPS.reachout(rem.id)}
              size="sm"
              variant="outline"
              className="rounded-full"
            >
              <ExternalLink aria-hidden="true" className="w-4 h-4 mr-2" />
              Open comparison
            </Button>
          </Link>
          <Button
            data-testid={FOLLOWUPS.done(rem.id)}
            size="sm"
            onClick={() => markDone(rem)}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <CheckCircle2 aria-hidden="true" className="w-4 h-4 mr-2" />
            Done
          </Button>
          <Button
            data-testid={FOLLOWUPS.delete(rem.id)}
            size="icon"
            variant="ghost"
            onClick={() => del(rem)}
            className="rounded-full text-muted-foreground hover:text-destructive"
          >
            <Trash2 aria-hidden="true" className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </li>
  );
}
